import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Region, RegionDto} from '../model/location/Region';
import {School, SchoolDto, SchoolType, SchoolTypeDto} from '../model/location/School';
import {Locale} from '../model/Locale';

@Injectable({
  providedIn: 'root'
})
export class LocationService {

  constructor(private http: HttpClient) {
  }

  private static readonly REGION_ID = 'REGION_ID';
  private static readonly SCHOOL_ID = 'SCHOOL_ID';
  private static readonly TYPE_ID = 'TYPE_ID';

  private static readonly REGIONS_BASE_URL = '/regions';
  private static readonly REGIONS_DETAILS_URL = `${LocationService.REGIONS_BASE_URL}/${LocationService.REGION_ID}`;
  private static readonly REGIONS_LIST_URL = `${LocationService.REGIONS_BASE_URL}?locale=`;

  private static readonly SCHOOLS_BASE_URL = '/schools';
  private static readonly SCHOOLS_TYPES_LIST_URL = `${LocationService.SCHOOLS_BASE_URL}/types?locale=`;
  private static readonly SCHOOLS_DETAILS_URL = `${LocationService.SCHOOLS_BASE_URL}/${LocationService.SCHOOL_ID}`;
  private static readonly REGION_SCHOOLS_BASE_URL = `${LocationService.REGIONS_DETAILS_URL}${LocationService.SCHOOLS_BASE_URL}`;
  private static readonly SCHOOLS_SEARCH_URL = `${LocationService.REGION_SCHOOLS_BASE_URL}/search`;

  getRegions(locale: Locale): Observable<Region[]> {
    const url = LocationService.REGIONS_LIST_URL + locale;

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => (data as RegionDto[]).map(item => Region.parse(item)))
      );
  }

  getSchoolTypes(locale: Locale): Observable<SchoolType[]> {
    const url = LocationService.SCHOOLS_TYPES_LIST_URL + locale;

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => (data as SchoolTypeDto[]).map(item => SchoolType.parse(item)))
      );
  }

  getSchoolDetails(schoolId: string): Observable<School> {
    const url = LocationService.SCHOOLS_DETAILS_URL.replace(LocationService.SCHOOL_ID, schoolId);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(item => School.parse(item as SchoolDto))
      );
  }

  createSchool(regionId: string, data: SchoolDto): Observable<School> {
    const url = LocationService.REGION_SCHOOLS_BASE_URL
      .replace(LocationService.REGION_ID, regionId);

    return this.http.post(`${environment.api.host}${url}`, data)
      .pipe(
        map(item => School.parse(item as SchoolDto))
      );
  }

  updateSchoolDetails(schoolId: string, data: SchoolDto): Observable<School> {
    const url = LocationService.SCHOOLS_DETAILS_URL.replace(LocationService.SCHOOL_ID, schoolId);

    return this.http.put(`${environment.api.host}${url}`, data)
      .pipe(
        map(item => School.parse(item as SchoolDto))
      );
  }

  deleteSchoolDetails(schoolId: string): Observable<object> {
    const url = LocationService.SCHOOLS_DETAILS_URL.replace(LocationService.SCHOOL_ID, schoolId);

    return this.http.delete(`${environment.api.host}${url}`);
  }

  getSchools(regionId: string, typeId?: string): Observable<School[]> {
    const url = LocationService.REGION_SCHOOLS_BASE_URL
      .replace(LocationService.REGION_ID, regionId) + (typeId ? `?type_id=${typeId}` : '');

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => (data as SchoolDto[]).map(item => School.parse(item)))
      );
  }

  searchSchools(regionId: string, typeId: string, query: string): Observable<School[]> {
    let url = LocationService.SCHOOLS_SEARCH_URL.replace(LocationService.REGION_ID, regionId);
    if (typeId && typeId != '-1') url += `?type_id=${typeId}`;

    const data = {
      query: query.trim()
    };

    return this.http.post(`${environment.api.host}${url}`, data)
      .pipe(
        map(data => (data as SchoolDto[]).map(item => School.parse(item)))
      );
  }

}
